import type { ComponentPropsWithoutRef, FC } from 'react'

import { Image } from '@skytech/ui/Image'
import { Td } from '@skytech/ui/Table/Td'
import { TrButton } from '@skytech/ui/Table/TrButton'
import CheckboxBlankOutlineIcon from 'mdi-react/CheckboxBlankOutlineIcon'
import CheckboxMarkedOutlineIcon from 'mdi-react/CheckboxMarkedOutlineIcon'
import PailOutlineIcon from 'mdi-react/PailOutlineIcon'
import RecycleIcon from 'mdi-react/RecycleIcon'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import Highlight from '@/components/Highlight'
import { LocalDate } from '@/components/LocalDate'
import { TrLink } from '@/components/TrLink'
import { USER_ROLE, useIsAdmin } from '@/hooks/access'
import useUser from '@/hooks/user'
import { APP_ROUTES } from '@/util/app-routes'
import { imagePlaceholder } from '@/util/misc'

import { AssetLink } from './AssetLink'

import type { Asset, AssetUserType } from '../../resources'

type ConditionalTrWrapperProps = {
  id: number
  showPopup?: boolean
  isSelected?: boolean
  onClick?: (id: number) => void
} & Omit<ComponentPropsWithoutRef<'tr'>, 'id'>

const ConditionalTrWrapper: FC<ConditionalTrWrapperProps> = ({
  children,
  id,
  showPopup = false,
  isSelected,
  onClick,
  ...props
}) => {
  const isAdmin = useIsAdmin()
  const user = useUser()

  if (showPopup && onClick) {
    return (
      <TrButton onClick={() => onClick(id)} className={`${isSelected ? 'selected' : ''}`}>
        {children}
      </TrButton>
    )
  }
  if (isAdmin || user?.role === USER_ROLE.IT_USER) {
    return <TrLink path={APP_ROUTES.HARDWARE_ASSET.replace(':id', String(id))}>{children}</TrLink>
  }
  return <tr {...props}>{children}</tr>
}

export const AssetListItem: FC<
  Asset & {
    leaseCurrentEndingOption?: string
    searchFilter: string
    isLeasedOwnershipType: boolean
    showLeasedFeatures: boolean
    isSelected?: boolean
    onSetAssetLinkPopupOpen: (open: boolean) => void
    onSetAssetLink: (link: string) => void
    onSelectLeasedAsset: (id: number) => void
  }
> = ({
  costObjectName,
  imageUrl,
  assetDescription,
  id,
  costObjectId,
  received,
  leaseStart,
  leaseEnd,
  cost,
  address,
  addressCity,
  addressZip,
  status,
  userType,
  imei,
  snr,
  assetType,
  leaseCurrentEndingOption,
  searchFilter,
  isLeasedOwnershipType,
  showLeasedFeatures,
  isSelected,
  onSetAssetLink,
  onSetAssetLinkPopupOpen,
  onSelectLeasedAsset,
}) => {
  const translate = useTranslation()
  const isAdmin = useIsAdmin()
  const user = useUser()

  return (
    <ConditionalTrWrapper
      id={id}
      showPopup={showLeasedFeatures}
      isSelected={isSelected}
      onClick={() => onSelectLeasedAsset(id)}
    >
      {showLeasedFeatures && (
        <Td className="center p-1 pr-0 md:table-cell md:p-2 md:pr-0 lg:p-2.5 lg:pr-0 xl:p-4">
          {isSelected ? <CheckboxMarkedOutlineIcon /> : <CheckboxBlankOutlineIcon />}
        </Td>
      )}
      <Td className="hidden md:p-2 lg:table-cell lg:p-2.5 xl:p-4">
        <div className="asset-name">
          <Image
            alt="asset"
            src={imageUrl || ''}
            className="hidden md:table-cell"
            onError={imagePlaceholder}
          />
          <AssetCategoryIcon userType={userType} />
        </div>
      </Td>

      <Td className="flex flex-col gap-1 md:gap-2 md:p-2 lg:p-2.5 xl:p-4">
        <div className="mb-1 whitespace-normal break-words md:mb-2.5">
          {showLeasedFeatures ? (
            <AssetLink
              className="whitespace-normal break-words text-start"
              searchFilter={searchFilter}
              assetText={assetDescription}
              showPopup={showLeasedFeatures}
              isLinkValid={(isAdmin || user?.role === USER_ROLE.IT_USER) && showLeasedFeatures}
              link={APP_ROUTES.HARDWARE_ASSET.replace(':id', String(id))}
              onAssetClick={() => {
                onSetAssetLink(APP_ROUTES.HARDWARE_ASSET.replace(':id', String(id)))
                onSetAssetLinkPopupOpen(true)
              }}
            />
          ) : (
            <Highlight pattern={searchFilter} text={assetDescription} />
          )}
        </div>
        <div className="table-cell text-[0.8rem] text-gray-400 md:hidden">
          <Highlight pattern={searchFilter} text={costObjectName} />
        </div>
        {isLeasedOwnershipType && (
          <div className="table-cell text-[0.8rem] text-gray-400 md:hidden">
            <FormattedNumberWithCurrency amount={cost || 0} />
          </div>
        )}
        <div className="flex flex-col gap-1">
          <div className="mt-[0.2em] hidden text-[0.8rem] text-gray-400 md:table-cell">
            <Highlight pattern={searchFilter} text={translate(`asset_type/${assetType}`)} />
          </div>
          {imei && (
            <div className="mt-[0.2em] hidden text-[0.8rem] text-gray-400 md:table-cell">
              <Translation id="IMEI" />: <Highlight pattern={searchFilter} text={imei} />
            </div>
          )}
          {snr && (
            <div className="mt-[0.2em] hidden text-[0.8rem] text-gray-400 md:table-cell">
              <Translation id="S/N" />: <Highlight pattern={searchFilter} text={snr} />
            </div>
          )}
        </div>
      </Td>

      <Td className="hidden md:table-cell md:p-2 lg:p-2.5 xl:p-4">
        <div>
          {costObjectName === 'Expired' ? (
            <Translation id="User unavailable" />
          ) : (
            <AssetLink
              searchFilter={searchFilter}
              assetText={costObjectName}
              showPopup={isLeasedOwnershipType}
              isLinkValid={isAdmin || user?.role === USER_ROLE.IT_USER}
              link={APP_ROUTES.EMPLOYEE.replace(':costObjectId', String(costObjectId))}
              onAssetClick={() => {
                onSetAssetLink(APP_ROUTES.EMPLOYEE.replace(':costObjectId', String(costObjectId)))
                onSetAssetLinkPopupOpen(true)
              }}
            />
          )}
        </div>
        <Highlight pattern={searchFilter} text={translate(`asset_user_type/${userType}`)} />
      </Td>

      <Td className="hidden md:table-cell md:p-2 lg:p-2.5 xl:p-4">
        <Highlight pattern={searchFilter} text={translate(`asset_status/${status}`)} />
      </Td>

      <Td className="hidden md:p-2 lg:table-cell lg:p-2.5 xl:p-4">
        <Highlight pattern={searchFilter} text={address} />
        <div className="mt-[0.2em] text-[0.8rem] text-gray-400">
          <Highlight pattern={searchFilter} text={addressZip} />{' '}
          <Highlight pattern={searchFilter} text={addressCity} />
        </div>
      </Td>

      {!isLeasedOwnershipType && (
        <Td className="hidden md:table-cell md:p-2 lg:p-2.5 xl:p-4">
          <LocalDate date={received} />
        </Td>
      )}

      {isLeasedOwnershipType && (
        <Td className="hidden sm:table-cell md:p-2 lg:p-2.5 xl:p-4">
          <LocalDate date={leaseStart} />
        </Td>
      )}
      {isLeasedOwnershipType && (
        <Td className="hidden sm:table-cell md:p-2 lg:p-2.5 xl:p-4">
          <LocalDate date={leaseEnd} />
          {leaseCurrentEndingOption && (
            <div className="mt-[0.2em] text-[0.8rem] text-gray-400">
              <Translation id={leaseCurrentEndingOption} />
            </div>
          )}
        </Td>
      )}

      <Td className="hidden md:table-cell md:p-2 lg:p-2.5 xl:p-4">
        <FormattedNumberWithCurrency amount={cost || 0} />
      </Td>
    </ConditionalTrWrapper>
  )
}

const AssetCategoryIcon: FC<{ userType: AssetUserType }> = ({ userType }) => {
  if (!['GREENPOOL', 'POOL'].includes(userType)) return null

  return (
    <div className="duration-250 absolute bottom-0 right-0 flex h-7 w-7 items-center justify-center overflow-hidden rounded-full bg-white p-1 transition ease-in">
      {userType === 'GREENPOOL' && <RecycleIcon className="ml-1 fill-green-500" />}
      {userType === 'POOL' && <PailOutlineIcon className="ml-1 fill-green-500" />}
    </div>
  )
}
